import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { DealOaApi } from '@openx/types/deal.ts';
import { AMAZON_DEMAND_PARTNER_ID } from '@openx/utils/services/config.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type RestrictionResult = {
  blocked: boolean;
  loading: boolean;
  reason?: string;
};

const getDemandPartnerIdFromDeal = (deal: DealOaApi | null): string | null => {
  const firstParticipant = deal?.deal_participants?.[0];
  return firstParticipant?.demand_partner ?? null;
};

const collectSeatIdsFromDeal = (deal: DealOaApi | null): string[] => {
  const firstParticipant = deal?.deal_participants?.[0];
  if (!firstParticipant) return [];

  const seatIds: string[] = [];

  if (firstParticipant.main_buyer_id) seatIds.push(String(firstParticipant.main_buyer_id));
  if (firstParticipant.buyer_id) seatIds.push(String(firstParticipant.buyer_id));

  const buyerIds = firstParticipant.buyer_ids;

  if (Array.isArray(buyerIds)) {
    seatIds.push(...buyerIds.filter(Boolean).map(String));
  } else if (buyerIds && typeof buyerIds === 'object') {
    seatIds.push(...Object.keys(buyerIds));
  }

  return seatIds;
};

export const useDealChangeStatusRestriction = (deal: DealOaApi | null): RestrictionResult => {
  const { t } = useTranslation();
  const { useDemandPartnersFetch, useBuyersFetch, useOnBehalfOfAccountId } = useDealContext();

  const onBehalfAccountId = useOnBehalfOfAccountId();

  const demandPartnerId = useMemo(() => getDemandPartnerIdFromDeal(deal), [deal]);

  const { mapById: demandPartnersMapById, loading: demandPartnersLoading } = useDemandPartnersFetch({
    account_uid: onBehalfAccountId,
  });

  const hasDspSelected = !!demandPartnerId;
  const dspOptionsLoaded = !demandPartnersLoading;

  const hasAccessToDsp = !hasDspSelected
    ? true
    : dspOptionsLoaded
      ? !!demandPartnersMapById?.[demandPartnerId!]
      : false;

  const canFetchBuyers = !!demandPartnerId && !!demandPartnersMapById?.[demandPartnerId];

  const { mapById: buyersMapById, loading: buyersLoading } = useBuyersFetch(canFetchBuyers ? demandPartnerId : null);

  const seatIds = useMemo(() => collectSeatIdsFromDeal(deal), [deal]);

  const isAmazonDemandPartner = demandPartnerId === AMAZON_DEMAND_PARTNER_ID;

  if (!hasDspSelected) {
    return { blocked: false, loading: false };
  }

  if (demandPartnersLoading) {
    return { blocked: true, loading: true };
  }

  if (!hasAccessToDsp) {
    return {
      blocked: true,
      loading: false,
      reason: t(
        "Your organization doesn't have access to this DSP or it was deactivated. Please contact OpenX if you require access."
      ),
    };
  }

  if (buyersLoading) {
    return { blocked: true, loading: true };
  }

  const buyerKeySet = new Set(Object.keys(buyersMapById ?? {}));
  // External users cannot fetch Amazon seats, so every seat on an Amazon deal looks unavailable here
  // and would block the status change.
  const hasInvalidSeat = !isAmazonDemandPartner && seatIds.some(seatId => !buyerKeySet.has(seatId));

  if (hasInvalidSeat) {
    return {
      blocked: true,
      loading: false,
      reason: t(
        "Your organization doesn't have access to this seat or it was deactivated. Please contact OpenX if you require access."
      ),
    };
  }

  return { blocked: false, loading: false };
};
